import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  UserPlus, PencilSimple, Trash, IdentificationCard, Phone, MagnifyingGlass, Wrench, Lock, Stack, Warning, Books, PaperPlaneTilt, Copy, WhatsappLogo, EnvelopeSimple,
} from "@phosphor-icons/react";
import PartQuotasDialog, { BulkQuotaDialog } from "@/components/PartQuotasDialog";
import TemplatesDialog from "@/components/TemplatesDialog";
import { fmtDate, fmtDateTime } from "@/lib/dates";

export default function TechniciansPage() {
  const { user } = useAuth();
  const canDelete = user.role === "supervisor";
  const isManager = user.role === "manager";
  const canChase = ["manager", "supervisor"].includes(user.role);
  const [chasing, setChasing] = useState(false);

  const chasePendingConfirmations = async () => {
    if (!window.confirm("Send a reminder email to every technician who hasn't confirmed this month's parts-in-hand list?")) return;
    setChasing(true);
    try {
      const res = await api.post("/reports/chase-monthly-inhand", {});
      const d = res.data || {};
      const bits = [
        `${d.sent || 0} reminder(s) sent`,
        `${d.skipped_already_acked || 0} already confirmed`,
        `${d.skipped_no_email || 0} no email`,
        `${d.failed || 0} failed`,
      ];
      toast.success(bits.join(" · "));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setChasing(false); }
  };
  const navigate = useNavigate();

  const [techs, setTechs] = useState([]);
  const [summaries, setSummaries] = useState({}); // {engineer_id: {held_items, held_units, open_records, returned_records, quota_count}}
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");   // 'all' | 'Technician' | 'Engineer'
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [quotaDialog, setQuotaDialog] = useState({ open: false, engineer: null });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRegionOpen, setBulkRegionOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [overQuota, setOverQuota] = useState([]);   // [{engineer_id, engineer_name, breaches, engineer_email, last_reminded_at}]
  const [overStripCollapsed, setOverStripCollapsed] = useState(false);
  const [remindingId, setRemindingId] = useState(null);   // engineer id currently being reminded, or 'all'

  const relativeTime = (iso) => {
    if (!iso) return null;
    try {
      const now = Date.now();
      const then = new Date(iso).getTime();
      const diffMin = Math.max(0, Math.round((now - then) / 60000));
      if (diffMin < 1) return "just now";
      if (diffMin < 60) return `${diffMin}m ago`;
      const h = Math.floor(diffMin / 60);
      if (h < 24) return `${h}h ago`;
      const d = Math.floor(h / 24);
      return `${d}d ago`;
    } catch { return null; }
  };

  const sendReminders = async ({ engineerIds = null, force = false } = {}) => {
    const scopeLabel = engineerIds ? `${engineerIds.length} technician(s)` : `${overQuota.length} technician(s)`;
    if (!engineerIds && !window.confirm(
      `Send return-reminder emails to ${scopeLabel}? Techs without an email on file will be flagged for manual follow-up.`,
    )) return;
    setRemindingId(engineerIds && engineerIds.length === 1 ? engineerIds[0] : "all");
    try {
      const body = { force };
      if (engineerIds) body.apply_to = engineerIds;
      const res = await api.post("/engineers/over-quota/remind", body);
      const { emailed = 0, no_email = 0, skipped_cooldown = 0, failed = 0 } = res.data || {};
      if (emailed) toast.success(`Emailed ${emailed} · no-email ${no_email} · cooldown ${skipped_cooldown} · failed ${failed}`);
      else if (failed) toast.error(`All ${failed} failed — check Gmail SMTP config in backend .env`);
      else if (skipped_cooldown) toast.info(`Skipped — all ${skipped_cooldown} in 24h cooldown. Use "Force resend" to override.`);
      else toast.success("Nothing to remind");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRemindingId(null); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [e, r, s, oq] = await Promise.all([
        api.get("/engineers"),
        api.get("/regions"),
        api.get("/engineers/summaries").catch(() => ({ data: [] })),
        api.get("/engineers/over-quota").catch(() => ({ data: [] })),
      ]);
      setTechs(e.data);
      setRegions(r.data);
      const map = {};
      (s.data || []).forEach((row) => { map[row.id] = row; });
      setSummaries(map);
      setOverQuota(oq.data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = techs;
    if (typeFilter !== "all") {
      out = out.filter((t) => (t.job_title || "Technician") === typeFilter);
    }
    if (q) {
      out = out.filter((t) =>
        t.name.toLowerCase().includes(q) ||
        (t.email || "").toLowerCase().includes(q) ||
        (t.employee_id || "").toLowerCase().includes(q) ||
        (t.phone || "").toLowerCase().includes(q) ||
        (t.region || "").toLowerCase().includes(q) ||
        (t.username || "").toLowerCase().includes(q),
      );
    }
    return out;
  }, [techs, search, typeFilter]);

  const counts = useMemo(() => {
    const c = { all: techs.length, Technician: 0, Engineer: 0 };
    techs.forEach((t) => {
      const jt = t.job_title || "Technician";
      c[jt] = (c[jt] || 0) + 1;
    });
    return c;
  }, [techs]);

  const missingRegionCount = useMemo(
    () => techs.filter((t) => !t.region).length,
    [techs],
  );

  // How many techs with holdings + a real email have NOT confirmed this month?
  // We infer "has holdings" via summaries.held_units > 0; without a valid email
  // the backend would skip them anyway, so we don't dangle a chase button.
  const pendingConfirmCount = useMemo(() => {
    const now = new Date();
    const cur = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    return techs.filter((t) => {
      const em = (t.email || "").trim();
      if (!em || em.endsWith(".internal")) return false;
      const s = summaries[t.id];
      if (!s || (s.held_units || 0) <= 0) return false;
      return t.inhand_acknowledged_month !== cur;
    }).length;
  }, [techs, summaries]);

  const del = async (t) => {
    if (!window.confirm(`Delete technician ${t.name}?`)) return;
    try {
      await api.delete(`/engineers/${t.id}`);
      toast.success("Technician deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen">
      <div className="border-b border-neutral-200 bg-white/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="px-10 py-6 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="label-eyebrow">Field staff</div>
            <h1 className="font-display text-4xl tracking-tight leading-none mt-2">Technicians &amp; Engineers</h1>
            <p className="text-sm text-neutral-500 mt-2 max-w-lg">
              Every field technician and engineer on the roster. Add, edit and track their on-hand inventory. Filter by job type below.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="label-eyebrow">On roster</div>
              <div className="font-display text-3xl" data-testid="tech-count">{techs.length}</div>
            </div>
            {canChase && (
              <Button
                onClick={chasePendingConfirmations}
                disabled={chasing || pendingConfirmCount === 0}
                data-testid="chase-inhand-btn"
                variant="outline"
                className="h-10 rounded-none border-amber-700 text-amber-800 hover:bg-amber-700 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                title={
                  pendingConfirmCount === 0
                    ? "All techs with holdings have confirmed this month — nothing to chase"
                    : `Resend this month's in-hand email to ${pendingConfirmCount} tech(s) who haven't confirmed yet`
                }
              >
                <EnvelopeSimple size={16} weight="bold" className="mr-2" />
                {chasing ? "Sending…" : `Chase pending (${pendingConfirmCount})`}
              </Button>
            )}
            {isManager && (
              <Button
                onClick={() => setTemplatesOpen(true)}
                data-testid="templates-btn"
                variant="outline"
                className="h-10 rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white"
              >
                <Books size={16} weight="bold" className="mr-2" /> Templates
              </Button>
            )}
            {isManager && (
              <Button
                onClick={() => setBulkOpen(true)}
                data-testid="bulk-quota-btn"
                variant="outline"
                className="h-10 rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white"
              >
                <Stack size={16} weight="bold" className="mr-2" /> Bulk Set Quota
              </Button>
            )}
            <Button
              onClick={() => setDialog({ open: true, editing: null })}
              data-testid="add-technician-btn"
              className="h-10 rounded-none bg-neutral-900 text-white hover:bg-neutral-800"
            >
              <UserPlus size={16} weight="bold" className="mr-2" /> Add Field Staff
            </Button>
          </div>
        </div>
        <div className="px-10 pb-4 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, username, employee ID, phone, region…"
              className="h-10 pl-9 rounded-none border-neutral-300 focus-visible:border-neutral-900 focus-visible:ring-0"
              data-testid="tech-search"
            />
          </div>
          <div className="flex border border-neutral-300" data-testid="tech-type-tabs">
            {[
              { key: "all", label: "All" },
              { key: "Technician", label: "Technicians" },
              { key: "Engineer", label: "Engineers" },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setTypeFilter(tab.key)}
                data-testid={`tech-tab-${tab.key.toLowerCase()}`}
                className={`h-10 px-4 text-xs font-mono uppercase tracking-wider border-r last:border-r-0 border-neutral-300 transition-colors ${
                  typeFilter === tab.key
                    ? "bg-neutral-900 text-white"
                    : "bg-white text-neutral-700 hover:bg-neutral-100"
                }`}
              >
                {tab.label} <span className={`ml-1 ${typeFilter === tab.key ? "text-neutral-300" : "text-neutral-400"}`}>{counts[tab.key] ?? 0}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {isManager && missingRegionCount > 0 && (
        <div className="px-10 pt-4" data-testid="missing-region-strip">
          <div className="border-l-4 border-amber-500 border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider">
              <Warning size={14} weight="fill" />
              {missingRegionCount} technician{missingRegionCount === 1 ? "" : "s"} without a home region — set below so their self-REQs pre-fill automatically
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setBulkRegionOpen(true)}
                className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-amber-700 bg-amber-700 text-white hover:bg-amber-800"
                data-testid="bulk-region-open"
              >
                Bulk assign ({filtered.length} filtered)
              </button>
              <button
                type="button"
                onClick={() => { setTypeFilter("all"); setSearch(""); }}
                className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-amber-600 text-amber-900 hover:bg-amber-100"
                data-testid="missing-region-jump"
              >
                Show all
              </button>
            </div>
          </div>
        </div>
      )}

      {overQuota.length > 0 && (
        <div className="px-10 pt-4" data-testid="over-quota-strip">
          <div className="border border-red-500 bg-red-50 text-red-900">
            <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-red-200 flex-wrap">
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider">
                <Warning size={14} weight="fill" />
                Over-quota alert — {overQuota.length} technician{overQuota.length === 1 ? " is" : "s are"} holding more than their cap
              </div>
              <div className="flex items-center gap-2">
                {isManager && (
                  <button
                    type="button"
                    onClick={() => sendReminders()}
                    disabled={remindingId === "all"}
                    className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-red-500 bg-red-900 text-white hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
                    data-testid="over-quota-remind-all"
                  >
                    <PaperPlaneTilt size={11} weight="bold" />
                    {remindingId === "all" ? "Sending…" : `Send reminders (${overQuota.length})`}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setOverStripCollapsed((v) => !v)}
                  className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-red-500 hover:bg-red-900 hover:text-white"
                  data-testid="over-quota-toggle"
                >
                  {overStripCollapsed ? "Show details" : "Hide details"}
                </button>
              </div>
            </div>
            {!overStripCollapsed && (
              <div className="divide-y divide-red-200 max-h-56 overflow-y-auto">
                {overQuota.map((row) => {
                  const lastReminded = relativeTime(row.last_reminded_at);
                  const busyRow = remindingId === row.engineer_id || remindingId === "all";
                  return (
                    <div
                      key={row.engineer_id}
                      className="px-3 py-2 flex items-center gap-3 text-sm"
                      data-testid={`over-quota-${row.engineer_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{row.engineer_name}</div>
                        <div className="text-[10px] font-mono text-red-800/80 flex items-center gap-2 flex-wrap">
                          <span>{row.employee_id ? `${row.employee_id} · ` : ""}{row.region || "—"}</span>
                          {row.engineer_email
                            ? <span className="opacity-80">· {row.engineer_email}</span>
                            : <span className="italic opacity-70">· no email — manual follow-up</span>}
                          {lastReminded && <span className="opacity-80">· last reminded {lastReminded}</span>}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 justify-end max-w-[50%]">
                        {row.breaches.map((b, i) => (
                          <span
                            key={i}
                            className="text-[10px] font-mono px-1.5 py-0.5 border border-red-500 bg-white text-red-900"
                            title={`Held ${b.held}, cap ${b.cap}, over by ${b.over_by}`}
                          >
                            {b.name} · {b.held}/{b.cap} <b>(+{b.over_by})</b>
                          </span>
                        ))}
                      </div>
                      {isManager && (
                        <button
                          type="button"
                          onClick={() => sendReminders({ engineerIds: [row.engineer_id], force: true })}
                          disabled={busyRow}
                          className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-red-500 hover:bg-red-900 hover:text-white whitespace-nowrap inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid={`over-quota-remind-${row.engineer_id}`}
                          title={row.engineer_email ? `Email ${row.engineer_email}` : "No email on file — will log as manual follow-up"}
                        >
                          <PaperPlaneTilt size={11} weight="bold" />
                          {remindingId === row.engineer_id ? "…" : "Remind"}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const t = techs.find((tt) => tt.id === row.engineer_id);
                          if (t) setQuotaDialog({ open: true, engineer: t });
                        }}
                        className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-red-500 hover:bg-red-900 hover:text-white whitespace-nowrap"
                        data-testid={`over-quota-open-${row.engineer_id}`}
                      >
                        Review
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="p-10">
        <div className="border border-neutral-200 bg-white">
          <table className="w-full text-sm" data-testid="technicians-table">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">Name</th>
                <th className="px-4 py-3 label-eyebrow">Type</th>
                <th className="px-4 py-3 label-eyebrow">Employee ID</th>
                <th className="px-4 py-3 label-eyebrow">Phone</th>
                <th className="px-4 py-3 label-eyebrow">Email</th>
                <th className="px-4 py-3 label-eyebrow">Region</th>
                <th className="px-4 py-3 label-eyebrow">Quota</th>
                <th className="px-4 py-3 label-eyebrow">Parts Summary</th>
                <th className="px-4 py-3 label-eyebrow">Added</th>
                <th className="px-4 py-3 label-eyebrow">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="px-4 py-8 text-center text-neutral-400">Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-neutral-400">
                  {techs.length === 0 ? "No field staff yet. Click 'Add Field Staff' to add your first." : "No matches for your search."}
                </td></tr>
              )}
              {filtered.map((t) => {
                const s = summaries[t.id] || { held_items: 0, held_units: 0, open_records: 0, returned_records: 0 };
                const jt = t.job_title || "Technician";
                // Monthly in-hand email — click-to-confirm indicator. Green pill
                // when the tech has acknowledged THIS calendar month, otherwise
                // a dim "pending" chip so supervisors can chase.
                const now = new Date();
                const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
                const ackMonth = t.inhand_acknowledged_month;
                const ackedThisMonth = ackMonth === currentMonthKey;
                const ackedAt = t.inhand_acknowledged_at ? new Date(t.inhand_acknowledged_at) : null;
                return (
                <tr key={t.id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60" data-testid={`tech-row-${t.id}`}>
                  <td className="px-4 py-3 font-medium">
                    <div>{t.name}</div>
                    {ackedThisMonth ? (
                      <div
                        className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider border border-emerald-700 bg-emerald-50 text-emerald-800"
                        data-testid={`tech-inhand-ack-${t.id}`}
                        title={`Confirmed monthly in-hand list on ${ackedAt ? fmtDateTime(ackedAt) : ackMonth}`}
                      >
                        <span aria-hidden>✓</span>
                        Confirmed {ackedAt ? fmtDate(ackedAt) : ackMonth}
                      </div>
                    ) : ackMonth ? (
                      <div
                        className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider border border-neutral-300 bg-neutral-50 text-neutral-500"
                        data-testid={`tech-inhand-ack-stale-${t.id}`}
                        title={`Last confirmed for ${ackMonth} — waiting on this month's confirmation`}
                      >
                        Pending {currentMonthKey}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider inline-flex items-center gap-1 ${
                        jt === "Engineer"
                          ? "bg-indigo-100 text-indigo-900"
                          : "bg-emerald-100 text-emerald-900"
                      }`}
                      data-testid={`tech-jobtitle-${t.id}`}
                    >
                      <Wrench size={10} weight="bold" /> {jt}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {t.employee_id ? (
                      <span className="inline-flex items-center gap-1"><IdentificationCard size={12} weight="duotone" /> {t.employee_id}</span>
                    ) : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {t.phone ? (
                      <a href={`tel:${t.phone}`} className="inline-flex items-center gap-1 hover:text-neutral-900 text-neutral-600"><Phone size={12} weight="duotone" /> {t.phone}</a>
                    ) : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-neutral-600">
                    <EmailCell tech={t} />
                  </td>
                  <td className="px-4 py-3">
                    <InlineRegionEditor tech={t} regions={regions} onSaved={load} />
                  </td>
                  <td className="px-4 py-3" data-testid={`tech-quota-${t.id}`}>
                    {s.quota_count > 0 ? (
                      <button
                        type="button"
                        onClick={() => setQuotaDialog({ open: true, engineer: t })}
                        className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-neutral-900 bg-neutral-900 text-white hover:bg-neutral-700 inline-flex items-center gap-1"
                        title="View / edit per-part quotas"
                      >
                        <Lock size={10} weight="bold" /> {s.quota_count} part{s.quota_count === 1 ? "" : "s"}
                      </button>
                    ) : (
                      <span className="text-[10px] font-mono text-neutral-400">no caps</span>
                    )}
                  </td>
                  <td className="px-4 py-3" data-testid={`tech-summary-${t.id}`}>
                    <div className="flex flex-wrap gap-1 items-center">
                      <button
                        type="button"
                        onClick={() => navigate(`/inventory?engineer=${t.id}`)}
                        title="View this technician's on-hand inventory"
                        className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${
                          s.held_units > 0
                            ? "border-neutral-900 bg-neutral-900 text-white hover:bg-neutral-700"
                            : "border-neutral-200 text-neutral-400 bg-neutral-50"
                        }`}
                        data-testid={`tech-held-${t.id}`}
                      >
                        {s.held_items} item{s.held_items === 1 ? "" : "s"} · {s.held_units} unit{s.held_units === 1 ? "" : "s"}
                      </button>
                      {s.open_records > 0 && (
                        <button
                          type="button"
                          onClick={() => navigate(`/records?status=open&engineer=${t.id}`)}
                          title="Open (unreturned) records"
                          className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200"
                          data-testid={`tech-open-${t.id}`}
                        >
                          {s.open_records} open
                        </button>
                      )}
                      {s.returned_records > 0 && (
                        <button
                          type="button"
                          onClick={() => navigate(`/records?status=returned&engineer=${t.id}`)}
                          title="Return history"
                          className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-emerald-50 text-emerald-900 border border-emerald-200 hover:bg-emerald-100"
                          data-testid={`tech-returned-${t.id}`}
                        >
                          {s.returned_records} returned
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-neutral-500">{fmtDate(t.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button
                        onClick={() => setQuotaDialog({ open: true, engineer: t })}
                        className="p-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
                        title={isManager ? "Edit part quotas" : "View part quotas"}
                        data-testid={`quotas-tech-${t.id}`}
                      >
                        <Lock size={14} weight="bold" />
                      </button>
                      <button
                        onClick={() => setDialog({ open: true, editing: t })}
                        className="p-1.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
                        title="Edit"
                        data-testid={`edit-tech-${t.id}`}
                      >
                        <PencilSimple size={14} weight="bold" />
                      </button>
                      {canDelete && (
                        <button
                          onClick={() => del(t)}
                          className="p-1.5 border border-neutral-300 hover:border-red-600 hover:bg-red-600 hover:text-white"
                          title="Delete"
                          data-testid={`delete-tech-${t.id}`}
                        >
                          <Trash size={14} weight="bold" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <TechnicianDialog
        open={dialog.open}
        editing={dialog.editing}
        regions={regions}
        onClose={() => setDialog({ open: false, editing: null })}
        onSaved={() => { setDialog({ open: false, editing: null }); load(); }}
      />

      <PartQuotasDialog
        open={quotaDialog.open}
        engineer={quotaDialog.engineer}
        isManager={isManager}
        onClose={() => setQuotaDialog({ open: false, engineer: null })}
        onSaved={() => { load(); }}
      />

      {isManager && (
        <BulkQuotaDialog
          open={bulkOpen}
          engineers={techs}
          onClose={() => setBulkOpen(false)}
          onSaved={() => { load(); }}
        />
      )}

      {isManager && (
        <TemplatesDialog
          open={templatesOpen}
          engineers={techs}
          onClose={() => setTemplatesOpen(false)}
          onSaved={() => { load(); }}
        />
      )}

      {isManager && (
        <BulkRegionDialog
          open={bulkRegionOpen}
          onClose={() => setBulkRegionOpen(false)}
          filteredTechs={filtered}
          regions={regions}
          onSaved={() => { setBulkRegionOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function TechnicianDialog({ open, editing, regions, onClose, onSaved }) {
  const isEdit = !!editing;
  const [form, setForm] = useState({
    name: "", phone: "", employee_id: "", region: "", job_title: "Technician", password: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: editing?.name || "",
        phone: editing?.phone || "",
        employee_id: editing?.employee_id || "",
        region: editing?.region || "",
        job_title: editing?.job_title || "Technician",
        password: "",
      });
    }
  }, [open, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        job_title: form.job_title,
        phone: form.phone.trim() || null,
        employee_id: form.employee_id.trim() || null,
        region: form.region || null,
      };
      if (form.password && form.password.trim()) {
        if (form.password.trim().length < 4) {
          setBusy(false);
          return toast.error("Password must be at least 4 characters");
        }
        payload.password = form.password.trim();
      }
      if (isEdit) {
        await api.patch(`/engineers/${editing.id}`, payload);
      } else {
        await api.post("/engineers", payload);
      }
      toast.success(
        isEdit
          ? (payload.password ? `${form.job_title} updated · new password saved` : `${form.job_title} updated`)
          : `${form.job_title} added${payload.password ? " · sign-in password set" : ""}`
      );
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-none border-neutral-900" data-testid="technician-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {isEdit ? `Edit ${form.job_title}` : "Add Field Staff"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="border border-neutral-200 bg-neutral-50 px-3 py-2 text-[11px] text-neutral-600">
            Field staff can now sign in with their <b>username</b> (auto-generated from employee ID or email) and the password you set below to raise parts requests and track pickup status.
          </div>
          <Field label="Full name" required>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-none border-neutral-400" data-testid="tech-name" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Job type" required>
              <Select value={form.job_title} onValueChange={(v) => set("job_title", v)}>
                <SelectTrigger data-testid="tech-job-title" className="rounded-none border-neutral-400"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Technician">Technician</SelectItem>
                  <SelectItem value="Engineer">Engineer</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Employee ID">
              <Input value={form.employee_id} onChange={(e) => set("employee_id", e.target.value)} placeholder="e.g. FF460" className="rounded-none border-neutral-400 font-mono" data-testid="tech-employee-id" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone">
              <Input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+60 12 345 6789" className="rounded-none border-neutral-400 font-mono" data-testid="tech-phone" />
            </Field>
            <Field label="Home region">
              <Select value={form.region || "none"} onValueChange={(v) => set("region", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="tech-region" className="rounded-none border-neutral-400"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {regions.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label={isEdit ? "Set / reset sign-in password (leave blank to keep current)" : "Sign-in password (optional — set now or later)"}>
            <Input
              type="text"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              placeholder={isEdit ? "•••••••• (unchanged)" : "e.g. Tech@123"}
              className="rounded-none border-neutral-400 font-mono"
              data-testid="tech-password"
              autoComplete="new-password"
            />
            <div className="text-[10px] text-neutral-500 mt-1">
              Share this manually with the technician. Shown as plain text here (managers only). Min 4 characters.
            </div>
          </Field>
          <div className="border border-neutral-200 bg-neutral-50 px-3 py-2 text-[11px] text-neutral-600">
            <b>Per-part quotas</b> are managed separately — save this record first, then click the <b>Lock icon</b> next to the row to set caps per part.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="tech-submit" className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800">
            {busy ? "Saving…" : (isEdit ? "Save Changes" : `Add ${form.job_title}`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, required = false }) {
  return (
    <div className="space-y-1.5">
      <Label className="label-eyebrow">
        {label}{required && <span className="text-red-600 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}

// -------------------------------------------------------------------------
// Email cell — clickable mailto, copy-to-clipboard, and WhatsApp login share.
// -------------------------------------------------------------------------
function EmailCell({ tech }) {
  const email = tech?.email;
  if (!email) return <span className="text-neutral-300">—</span>;

  const identifier = tech.username || email;
  const shareText =
    `Hi ${tech.name},\n\n` +
    `Your FlowFuel Stock login:\n` +
    `• Login ID: ${identifier}\n` +
    `• Password: Tech@123 (change on first login)\n\n` +
    `Sign in at https://flofuel.com/login\n` +
    `Any issues, ping the workshop.`;

  const copy = async (val, msg = "Copied") => {
    try {
      await navigator.clipboard.writeText(val);
      toast.success(msg);
    } catch {
      toast.error("Copy failed — long-press to select text");
    }
  };

  const openWhatsApp = () => {
    const phone = (tech.phone || "").replace(/[^\d]/g, "");
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(shareText)}`
      : `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <div className="inline-flex items-center gap-1.5 group" data-testid={`tech-email-${tech.id}`}>
      <a
        href={`mailto:${email}`}
        className="hover:text-neutral-900 truncate max-w-[220px]"
        title={email}
      >
        {email}
      </a>
      <button
        type="button"
        onClick={() => copy(email, `Copied ${email}`)}
        className="p-1 border border-transparent hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
        title="Copy email"
        data-testid={`copy-email-${tech.id}`}
      >
        <Copy size={12} weight="bold" />
      </button>
      <button
        type="button"
        onClick={openWhatsApp}
        className="p-1 border border-transparent hover:border-emerald-600 hover:bg-emerald-600 hover:text-white transition-colors"
        title={`Share login via WhatsApp${tech.phone ? ` (${tech.phone})` : ""}`}
        data-testid={`whatsapp-share-${tech.id}`}
      >
        <WhatsappLogo size={12} weight="bold" />
      </button>
    </div>
  );
}


// -------------------------------------------------------------------------
// InlineRegionEditor — Manager/Admin can set a technician's home region
// directly in the table so their self-REQs pre-fill the correct region.
// -------------------------------------------------------------------------
function InlineRegionEditor({ tech, regions, onSaved }) {
  const { user } = useAuth();
  const canEdit = ["manager", "admin", "supervisor"].includes(user?.role);
  const [busy, setBusy] = useState(false);

  const save = async (newRegion) => {
    if (newRegion === tech.region) return;
    setBusy(true);
    try {
      await api.patch(`/engineers/${tech.id}`, { region: newRegion || null });
      toast.success(`${tech.name} → ${newRegion || "no region"}`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!canEdit) {
    return tech.region
      ? <span>{tech.region}</span>
      : <span className="text-neutral-400">—</span>;
  }

  const missing = !tech.region;
  return (
    <Select
      value={tech.region || "__none__"}
      onValueChange={(v) => save(v === "__none__" ? "" : v)}
      disabled={busy}
    >
      <SelectTrigger
        data-testid={`inline-region-${tech.id}`}
        className={`rounded-none h-8 text-xs w-40 ${
          missing
            ? "border-amber-500 bg-amber-50 text-amber-900 font-medium animate-pulse"
            : "border-neutral-300"
        }`}
        title={missing ? "No region set — technician can't pre-fill their self-REQ. Pick one." : "Change home region"}
      >
        <SelectValue placeholder="Set region" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__"><span className="text-neutral-500 italic">— No region —</span></SelectItem>
        {regions.map((r) => (
          <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}


// -------------------------------------------------------------------------
// BulkRegionDialog — stamp one region onto a filtered subset of technicians.
// -------------------------------------------------------------------------
function BulkRegionDialog({ open, onClose, filteredTechs, regions, onSaved }) {
  const [region, setRegion] = useState("__none__");
  const [scope, setScope] = useState("missing"); // "missing" | "all-filtered"
  const [busy, setBusy] = useState(false);

  const missingCount = filteredTechs.filter((t) => !t.region).length;
  const targetTechs = scope === "missing"
    ? filteredTechs.filter((t) => !t.region)
    : filteredTechs;

  const submit = async () => {
    if (targetTechs.length === 0) { toast.error("No technicians in scope"); return; }
    const regionName = region === "__none__" ? null : region;
    const label = regionName || "(cleared)";
    if (!window.confirm(
      `Stamp region "${label}" on ${targetTechs.length} technician${targetTechs.length === 1 ? "" : "s"}?\n\n` +
      `This overwrites existing regions in scope.`,
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/engineers/bulk-set-region", {
        region: regionName,
        only_ids: targetTechs.map((t) => t.id),
      });
      toast.success(`Updated ${data.updated} of ${data.total} technicians`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-none border-neutral-900" data-testid="bulk-region-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <IdentificationCard size={22} weight="duotone" className="text-amber-600" />
            Bulk Assign Home Region
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border border-neutral-300 bg-neutral-50 px-3 py-2 text-xs text-neutral-700">
            Currently filtered: <b>{filteredTechs.length}</b> technicians · <b>{missingCount}</b> without a region.
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Scope</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setScope("missing")}
                data-testid="bulk-region-scope-missing"
                className={`h-11 border-2 text-sm font-medium transition-colors ${
                  scope === "missing"
                    ? "border-amber-500 bg-amber-500 text-white"
                    : "border-neutral-300 bg-white hover:border-amber-500"
                }`}
              >
                Only missing ({missingCount})
              </button>
              <button
                type="button"
                onClick={() => setScope("all-filtered")}
                data-testid="bulk-region-scope-all"
                className={`h-11 border-2 text-sm font-medium transition-colors ${
                  scope === "all-filtered"
                    ? "border-neutral-900 bg-neutral-900 text-white"
                    : "border-neutral-300 bg-white hover:border-neutral-900"
                }`}
              >
                All filtered ({filteredTechs.length})
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Home region to assign</Label>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger data-testid="bulk-region-select" className="rounded-none border-neutral-400"><SelectValue placeholder="Pick region" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__"><span className="text-neutral-500 italic">— No region (clear) —</span></SelectItem>
                {regions.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="text-[11px] text-neutral-500 leading-relaxed">
            This runs a single Mongo update — safe on 55+ technicians. Existing regions in scope will be overwritten. Their next self-REQ will pre-fill this region automatically.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || targetTechs.length === 0}
            data-testid="bulk-region-submit"
            className="rounded-none bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40"
          >
            {busy ? "Stamping…" : `Stamp region on ${targetTechs.length}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

